/**
 * Order timeline: when the order was requested, accepted, completed — and who did each step.
 */
import { Alert, Box, Card, CardContent, Chip, Divider, Typography } from '@mui/material';
import { Timeline as TimelineIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useGetOrderTimelineQuery, type TimelineEvent } from '../../store/api/detailedReportsApi';
import { palette } from '../../theme/palette';

const dotColor = (event: TimelineEvent) => {
  if (event.event_type === 'STATUS_CHANGED') {
    if (event.to_value === 'COMPLETED') return palette.status.success;
    if (['CANCELLED', 'REJECTED', 'REFUNDED'].includes(event.to_value)) return palette.status.danger;
  }
  if (event.event_type === 'PAYMENT_STATUS') return palette.status.info;
  return palette.brand[500];
};

const OrderTimeline = ({ orderId }: { orderId: number }) => {
  const { t } = useTranslation();
  const { data } = useGetOrderTimelineQuery(orderId);
  const timeline = data?.data;
  if (!timeline) return null;

  const status = (value: string) => (value ? t(`components.status.${value}`, { defaultValue: value }) : '—');
  const payment = (value: string) => (value ? t(`orders.timeline.payment.${value}`, { defaultValue: value }) : '—');

  const describe = (e: TimelineEvent) => {
    switch (e.event_type) {
      case 'CREATED':
        return t('orders.timeline.events.CREATED');
      case 'STATUS_CHANGED':
        return t('orders.timeline.events.STATUS_CHANGED', { from: status(e.from_value), to: status(e.to_value) });
      case 'PROVIDER_ASSIGNED':
        return t('orders.timeline.events.PROVIDER_ASSIGNED', { name: e.to_value });
      case 'PROVIDER_CHANGED':
        return t('orders.timeline.events.PROVIDER_CHANGED', { from: e.from_value, to: e.to_value });
      case 'PROVIDER_REMOVED':
        return t('orders.timeline.events.PROVIDER_REMOVED', { name: e.from_value });
      case 'PAYMENT_STATUS':
        return e.from_value
          ? t('orders.timeline.events.PAYMENT_CHANGED', { from: payment(e.from_value), to: payment(e.to_value) })
          : t('orders.timeline.events.PAYMENT_CREATED', { status: payment(e.to_value) });
      default:
        return e.event_type;
    }
  };

  const duration = (seconds: number | null) =>
    seconds === null ? null : moment.duration(seconds, 'seconds').humanize();

  const { summary } = timeline;
  const milestones = [
    { label: t('orders.timeline.requested'), at: summary.requested_at, after: null },
    { label: t('orders.timeline.accepted'), at: summary.accepted_at, after: duration(summary.seconds_to_accept) },
    { label: t('orders.timeline.completed'), at: summary.completed_at, after: duration(summary.seconds_to_complete) },
  ];

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TimelineIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            {t('orders.timeline.title')}
          </Typography>
        </Box>

        {/* Key milestones */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {milestones.map((m) => (
            <Box key={m.label} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {m.label}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {m.at ? moment(m.at).format('DD MMM YYYY · HH:mm') : t('orders.timeline.notYet')}
              </Typography>
              {m.at && m.after && (
                <Typography variant="caption" color="text.secondary">
                  {t('orders.timeline.afterRequest', { duration: m.after })}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {timeline.has_untracked_history && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('orders.timeline.untracked')}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Event list */}
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {timeline.events.map((e, index) => (
            <Box
              component="li"
              key={e.id ?? `historical-${index}`}
              sx={{ display: 'flex', gap: 2, position: 'relative', pb: index === timeline.events.length - 1 ? 0 : 2.5 }}
            >
              {/* dot + connecting line */}
              <Box sx={{ position: 'relative', width: 14, flexShrink: 0 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: dotColor(e), mt: 0.6 }} />
                {index < timeline.events.length - 1 && (
                  <Box
                    sx={{ position: 'absolute', top: 20, bottom: -4, insetInlineStart: 5, width: 2, bgcolor: 'divider' }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {describe(e)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {moment(e.created_at).format('DD MMM YYYY · HH:mm')}
                  </Typography>
                  {(e.actor_name || e.is_historical) && (
                    <Typography variant="caption" color="text.secondary">
                      · {t('orders.timeline.by', { name: e.actor_name || t('orders.timeline.unknown') })}
                    </Typography>
                  )}
                  {e.source && (
                    <Chip size="small" variant="outlined" label={t(`orders.timeline.source.${e.source}`)} sx={{ height: 20 }} />
                  )}
                </Box>
                {e.note && e.event_type === 'PAYMENT_STATUS' && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {e.note}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default OrderTimeline;
